import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAccountInfo, changePassword, deleteAccount, updateEmailPreferences, updateName, exportData } from '../../api/account';
import styles from './AccountPage.module.css';

const EyeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const AccountPage = () => {
    const navigate = useNavigate();
    const { logout, updateUser } = useAuth();

    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);

    // Name editing
    const [editingName, setEditingName] = useState(false);
    const [nameValue, setNameValue] = useState('');
    const [nameLoading, setNameLoading] = useState(false);
    const [nameSuccess, setNameSuccess] = useState('');
    const [nameError, setNameError] = useState('');

    // Password form
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Email preferences
    const [weeklyDigest, setWeeklyDigest] = useState(true);
    const [emailLoading, setEmailLoading] = useState(false);

    // Data export
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState('');

    // Delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    const loadAccount = useCallback(async () => {
        try {
            const data = await fetchAccountInfo();
            setAccount(data.user);
            setNameValue(data.user.name || '');
            setWeeklyDigest(!data.user.weeklyEmailOptOut);
        } catch (err) {
            console.error('Failed to load account info:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAccount();
    }, [loadAccount]);

    const handleSaveName = async () => {
        setNameSuccess('');
        setNameError('');
        setNameLoading(true);
        try {
            const result = await updateName(nameValue);
            setNameSuccess(result.message);
            setAccount(prev => ({ ...prev, name: result.name }));
            updateUser({ name: result.name });
            setEditingName(false);
        } catch (err) {
            setNameError(err.response?.data?.message || 'Failed to update name');
        } finally {
            setNameLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordSuccess('');
        setPasswordError('');

        if (newPassword !== confirmNewPassword) {
            setPasswordError('New passwords do not match');
            return;
        }

        setPasswordLoading(true);
        try {
            const result = await changePassword(currentPassword, newPassword, confirmNewPassword);
            setPasswordSuccess(result.message);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (err) {
            setPasswordError(err.response?.data?.message || 'Failed to change password');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleToggleDigest = async () => {
        const newValue = !weeklyDigest;
        setWeeklyDigest(newValue);
        setEmailLoading(true);
        try {
            await updateEmailPreferences(newValue);
        } catch (err) {
            setWeeklyDigest(!newValue);
            console.error('Failed to update email preferences:', err);
        } finally {
            setEmailLoading(false);
        }
    };

    const handleExportData = async () => {
        setExportError('');
        setExportLoading(true);
        try {
            const blob = await exportData();
            const dateStr = new Date().toISOString().split('T')[0];
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `teachio-data-export-${dateStr}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setExportError('Failed to export data. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleteError('');
        setDeleteLoading(true);
        try {
            await deleteAccount(deletePassword);
            await logout();
            navigate('/');
        } catch (err) {
            setDeleteError(err.response?.data?.message || 'Failed to delete account');
        } finally {
            setDeleteLoading(false);
        }
    };

    const canDelete = deletePassword.length > 0 && deleteConfirmText === 'DELETE';

    if (loading) {
        return <div className={styles.loading}>Loading account...</div>;
    }

    return (
        <div className={styles.page}>
            <h1 className={styles.title}>Account Settings</h1>

            {/* Profile */}
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Profile</h2>
                <div className={styles.profileRow}>
                    <span className={styles.profileLabel}>Name</span>
                    {editingName ? (
                        <div className={styles.nameEditRow}>
                            <input
                                className={styles.nameInput}
                                type="text"
                                value={nameValue}
                                onChange={(e) => setNameValue(e.target.value)}
                                placeholder="Ms. Johnson, Sarah, etc."
                                maxLength={50}
                                autoFocus
                            />
                            <button
                                className={styles.nameSaveButton}
                                onClick={handleSaveName}
                                disabled={nameLoading || !nameValue.trim()}
                            >
                                {nameLoading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                className={styles.nameCancelButton}
                                onClick={() => {
                                    setEditingName(false);
                                    setNameValue(account?.name || '');
                                    setNameError('');
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className={styles.nameDisplayRow}>
                            <span className={styles.profileValue}>
                                {account?.name || <span className={styles.notSet}>Not set</span>}
                            </span>
                            <button
                                className={styles.editButton}
                                onClick={() => setEditingName(true)}
                            >
                                Edit
                            </button>
                        </div>
                    )}
                </div>
                {nameSuccess && <div className={styles.successMessage}>{nameSuccess}</div>}
                {nameError && <div className={styles.errorMessage}>{nameError}</div>}
                <div className={styles.profileRow}>
                    <span className={styles.profileLabel}>Email</span>
                    <span className={styles.profileValue}>{account?.email}</span>
                </div>
                <div className={styles.profileRow}>
                    <span className={styles.profileLabel}>Member since</span>
                    <span className={styles.profileValue}>
                        {account?.createdAt && new Date(account.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        })}
                    </span>
                </div>
            </div>

            {/* Change Password */}
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Change Password</h2>
                <form className={styles.form} onSubmit={handleChangePassword}>
                    <div className={styles.fieldGroup}>
                        <label className={styles.label}>Current Password</label>
                        <div className={styles.inputWrapper}>
                            <input
                                className={styles.input}
                                type={showCurrentPassword ? 'text' : 'password'}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className={styles.togglePassword}
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                            >
                                {showCurrentPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </button>
                        </div>
                    </div>
                    <div className={styles.fieldGroup}>
                        <label className={styles.label}>New Password</label>
                        <div className={styles.inputWrapper}>
                            <input
                                className={styles.input}
                                type={showNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className={styles.togglePassword}
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                            >
                                {showNewPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </button>
                        </div>
                    </div>
                    <div className={styles.fieldGroup}>
                        <label className={styles.label}>Confirm New Password</label>
                        <div className={styles.inputWrapper}>
                            <input
                                className={styles.input}
                                type={showNewPassword ? 'text' : 'password'}
                                value={confirmNewPassword}
                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    {passwordSuccess && <div className={styles.successMessage}>{passwordSuccess}</div>}
                    {passwordError && <div className={styles.errorMessage}>{passwordError}</div>}
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={passwordLoading || !currentPassword || !newPassword || !confirmNewPassword}
                    >
                        {passwordLoading ? 'Changing...' : 'Change Password'}
                    </button>
                </form>
            </div>

            {/* Email Preferences */}
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Email Preferences</h2>
                <div className={styles.toggleRow}>
                    <div className={styles.toggleInfo}>
                        <span className={styles.toggleLabel}>Weekly lesson summary</span>
                        <span className={styles.toggleDescription}>
                            Receive a weekly email with your lesson plan activity
                        </span>
                    </div>
                    <label className={styles.toggle}>
                        <input
                            type="checkbox"
                            checked={weeklyDigest}
                            onChange={handleToggleDigest}
                            disabled={emailLoading}
                        />
                        <span className={styles.toggleSlider} />
                    </label>
                </div>
            </div>

            {/* Data & Privacy */}
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Data & Privacy</h2>
                <p className={styles.sectionDescription}>
                    Download a copy of all your data including lessons, classes, worksheets, and conversations.
                </p>
                {exportError && <div className={styles.errorMessage}>{exportError}</div>}
                <button
                    className={styles.submitButton}
                    onClick={handleExportData}
                    disabled={exportLoading}
                >
                    {exportLoading ? 'Preparing download...' : 'Download My Data'}
                </button>
            </div>

            {/* Danger Zone */}
            <div className={styles.dangerCard}>
                <h2 className={styles.dangerTitle}>Danger Zone</h2>
                <p className={styles.dangerText}>
                    Permanently delete your account and all associated data including lessons, classes, and conversations. This action cannot be undone.
                </p>
                <button className={styles.dangerButton} onClick={() => setShowDeleteModal(true)}>
                    Delete Account
                </button>
            </div>

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 className={styles.modalTitle}>Delete Account</h3>
                        <p className={styles.modalText}>
                            This will permanently delete your account and all your data. This action cannot be undone.
                        </p>
                        <div className={styles.form}>
                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>Enter your password</label>
                                <input
                                    className={styles.input}
                                    type="password"
                                    value={deletePassword}
                                    onChange={(e) => setDeletePassword(e.target.value)}
                                    placeholder="Your password"
                                />
                            </div>
                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>Type DELETE to confirm</label>
                                <input
                                    className={styles.input}
                                    type="text"
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                    placeholder="DELETE"
                                />
                            </div>
                            {deleteError && <div className={styles.errorMessage}>{deleteError}</div>}
                        </div>
                        <div className={styles.modalActions}>
                            <button
                                className={styles.cancelButton}
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeletePassword('');
                                    setDeleteConfirmText('');
                                    setDeleteError('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.confirmDeleteButton}
                                disabled={!canDelete || deleteLoading}
                                onClick={handleDeleteAccount}
                            >
                                {deleteLoading ? 'Deleting...' : 'Delete My Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountPage;
